import {
  computePosition,
  flip,
  offset,
  shift,
  size,
  autoUpdate,
  Placement,
  Strategy,
} from "@floating-ui/dom";

/**
 * Represents the gap between a floating element and its reference element, in
 * pixels.
 */
const TRIGGER_OFFSET = 4;

/*
 * Tracks the active positioning cleanup for each floating element. Both
 * `$anchor` and any consumer that takes over positioning (e.g. the popover
 * component's `openAtCoordinates`) register here so that whoever installs
 * next can evict the prior watcher in a single call -- preventing the
 * `autoUpdate` leak that otherwise stacks one watcher per call for the
 * lifetime of the floating element.
 */
const anchorCleanups = new WeakMap<HTMLElement, () => void>();

/**
 * Disposes the active positioning cleanup on the given floating element, if
 * any. No-op if the element has no registered cleanup.
 */
export function disposeAnchor(floatingElement: HTMLElement): void {
  const cleanup = anchorCleanups.get(floatingElement);
  if (!cleanup) return;
  /*
   * Delete before invoking so the cleanup can't observe itself in the map
   * (e.g. if it inspects state during teardown).
   */
  anchorCleanups.delete(floatingElement);
  cleanup();
}

/**
 * Registers `cleanup` as the active positioning cleanup for `floatingElement`,
 * disposing any prior cleanup first. Consumers that take over positioning of
 * an already-anchored element should use this so that any subsequent `$anchor`
 * call (or other consumer) automatically evicts them rather than fighting
 * over `floatingElement.style`.
 */
export function registerAnchorCleanup(
  floatingElement: HTMLElement,
  cleanup: () => void
): void {
  disposeAnchor(floatingElement);
  anchorCleanups.set(floatingElement, cleanup);
}

/**
 * Magic function that wraps Floating UI's anchor positioning utilities.
 *
 * Takes in a reference element, a floating element, and a default placement of
 * the floating element relative to the reference element. If the floating
 * element overflows, the next best placement will be used via the flip() and
 * shift() middlewares.
 *
 * The autoUpdate() method is used to ensure the floating element remains
 * anchored to its reference element, such as when scrolling and resizing the
 * screen, as its position needs to be continually updated when necessary.
 *
 * Calling `$anchor` on a floating element that already has an active anchor
 * silently disposes the prior watcher before installing a fresh one — see
 * `disposeAnchor` for details.
 *
 * https://floating-ui.com/
 */
export default () =>
  (
    referenceElement: HTMLElement,
    floatingElement: HTMLElement,
    placement: Placement = "bottom-start",
    strategy: Strategy = "absolute",
    matchReferenceWidth: Boolean = false
  ) => {
    floatingElement.style.display = "block";

    if (!placement) {
      placement = "bottom-start";
    }

    if (!strategy) {
      strategy = "absolute";
    }

    const cleanup = autoUpdate(referenceElement, floatingElement, () => {
      computePosition(referenceElement, floatingElement, {
        placement: placement,
        strategy: strategy,
        middleware: [
          offset(TRIGGER_OFFSET),
          flip(),
          shift(),
          size({
            apply({ rects }) {
              if (matchReferenceWidth) {
                Object.assign(floatingElement.style, {
                  width: `${rects.reference.width}px`,
                });
              }
            },
          }),
        ],
      }).then(({ x, y }) => {
        Object.assign(floatingElement.style, {
          position: `${strategy}`,
          left: `${x}px`,
          top: `${y}px`,
        });
      });
    });

    registerAnchorCleanup(floatingElement, cleanup);
  };
